import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label
} from 'recharts';
import { ETHNICITY_COLORS, QUADRANT_THRESHOLDS, formatPercentage } from '../../../../lib/utils/deepDiveUtils';
import QuadrantDetailTable from './QuadrantDetailTable';

const AwareInterestedQuadrant = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No quadrant data available
        </Typography>
      </Paper>
    );
  }

  // Transform data for scatter plot
  const scatterData = data.map(record => ({
    x: record.PctAware || 0,
    y: record.PctInterest || 0,
    size: record.totalResponses || 10,
    ethnicity: record.ethnicity || 'Unknown',
    agegroup: record.agegroup || 'Unknown',
    gender: record.gender || 'Unknown',
    responses: record.totalResponses || 0,
    movieFrequency: record.movie_frequency_category || 'Unknown'
  }));

  // Calculate quadrant distributions
  const quadrants = {
    'Prime Audience': { data: [], total: 0 },
    'Resistant': { data: [], total: 0 },
    'Hidden Opportunity': { data: [], total: 0 },
    'Cold Audience': { data: [], total: 0 }
  };

  scatterData.forEach(point => {
    const aware = point.x >= QUADRANT_THRESHOLDS.aware;
    const interested = point.y >= QUADRANT_THRESHOLDS.interested;
    
    let quadrant;
    if (aware && interested) quadrant = 'Prime Audience';
    else if (aware && !interested) quadrant = 'Resistant';
    else if (!aware && interested) quadrant = 'Hidden Opportunity';
    else quadrant = 'Cold Audience';

    quadrants[quadrant].data.push(point);
    quadrants[quadrant].total += point.responses;
  });

  const totalResponses = Object.values(quadrants).reduce((sum, q) => sum + q.total, 0);

  // Quadrant metadata
  const quadrantInfo = [
    {
      name: 'Prime Audience',
      color: '#4caf50',
      description: 'Aware + Interested',
      position: 'Top Right',
      strategy: 'Convert to sales'
    },
    {
      name: 'Resistant',
      color: '#f44336',
      description: 'Aware but NOT Interested',
      position: 'Top Left',
      strategy: 'Improve messaging'
    },
    {
      name: 'Hidden Opportunity',
      color: '#ffc107',
      description: 'NOT Aware but Interested',
      position: 'Bottom Right',
      strategy: 'Awareness campaigns'
    },
    {
      name: 'Cold Audience',
      color: '#757575',
      description: 'Neither Aware nor Interested',
      position: 'Bottom Left',
      strategy: 'Education & discovery'
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.ethnicity} - {data.agegroup} {data.gender}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Awareness: <strong>{formatPercentage(data.x)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Interest: <strong>{formatPercentage(data.y)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {data.movieFrequency}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Aware vs Interested Quadrant Analysis
      </Typography>

      <Grid container spacing={3}>
        {/* Scatter Plot */}
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name="Awareness"
                    domain={[0, 100]}
                    label={{
                      value: 'Awareness %',
                      position: 'bottom',
                      offset: 40,
                      style: { fontWeight: 600, fontSize: '0.9rem' }
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="Interest"
                    domain={[0, 100]}
                    label={{
                      value: 'Interest %',
                      angle: -90,
                      position: 'left',
                      offset: 40,
                      style: { fontWeight: 600, fontSize: '0.9rem' }
                    }}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  
                  {/* Reference lines for quadrants */}
                  <ReferenceLine
                    x={QUADRANT_THRESHOLDS.aware}
                    stroke="#999"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <ReferenceLine
                    y={QUADRANT_THRESHOLDS.interested}
                    stroke="#999"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />

                  {/* Quadrant Labels - Positioned correctly in each quadrant */}
                  {/* Top Right: Prime Audience (aware AND interested) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.aware + 25} strokeWidth={0}>
                    <Label 
                      value="PRIME AUDIENCE" 
                      position={{x: '50%', y: 75}} 
                      fill="#4caf50" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>
                  
                  {/* Top Left: Hidden Opportunity (NOT aware but interested) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.aware - 25} strokeWidth={0}>
                    <Label 
                      value="HIDDEN OPPORTUNITY" 
                      position={{x: '50%', y: 75}} 
                      fill="#ffc107" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>
                  
                  {/* Bottom Right: Resistant (aware but NOT interested) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.aware + 25} strokeWidth={0}>
                    <Label 
                      value="RESISTANT" 
                      position={{x: '50%', y: 25}} 
                      fill="#f44336" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>
                  
                  {/* Bottom Left: Cold Audience (neither aware nor interested) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.aware - 25} strokeWidth={0}>
                    <Label 
                      value="COLD AUDIENCE" 
                      position={{x: '50%', y: 25}} 
                      fill="#757575" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>

                  <Scatter data={scatterData}>
                    {scatterData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={ETHNICITY_COLORS[entry.ethnicity] || '#757575'}
                        opacity={0.7}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>

              {/* Legend */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                {Object.keys(ETHNICITY_COLORS).map(ethnicity => (
                  <Box key={ethnicity} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: ETHNICITY_COLORS[ethnicity]
                      }}
                    />
                    <Typography variant="caption">{ethnicity}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quadrant Summary Cards */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            {quadrantInfo.map(quadrant => {
              const quadrantData = quadrants[quadrant.name];
              const percentage = totalResponses > 0
                ? Math.round((quadrantData.total / totalResponses) * 100)
                : 0;

              return (
                <Grid item xs={12} sm={6} lg={12} key={quadrant.name}>
                  <Card
                    sx={{
                      border: `2px solid ${quadrant.color}`,
                      backgroundColor: quadrant.color + '05'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: quadrant.color,
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {quadrant.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {quadrant.description}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: quadrant.color }}>
                        {percentage}%
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          p: 0.5,
                          backgroundColor: quadrant.color + '15',
                          borderRadius: 1,
                          fontStyle: 'italic'
                        }}
                      >
                        Strategy: {quadrant.strategy}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Detailed Breakdown Tables */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Detailed Demographic Breakdown by Quadrant
          </Typography>
          <Grid container spacing={2}>
            {quadrantInfo.map(quadrant => (
              <Grid item xs={12} md={6} key={quadrant.name}>
                <QuadrantDetailTable
                  quadrantName={quadrant.name}
                  quadrantColor={quadrant.color}
                  data={quadrants[quadrant.name].data}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AwareInterestedQuadrant;

